import React, { useMemo } from "react";
import { Table, Typography } from "@optimizely/axiom";

import styles from "./BulkUploadDetails.module.scss";
import { BulkUpload } from "../../../domain/BulkUpload";
import { EMPTY_VALUE } from "../../../lib/bulk-upload-format";
import { UserGroup } from "../../../domain/UserGroup";
import { TableSkeleton } from "../Skeleton/TableSkeleton";
import { UserGroupTypeBadge } from "../UserGroupTypeBadge/UserGroupTypeBadge";
import { useUserGroupsByIds } from "../../../hooks/useUserGroupsByIds/useUserGroupsByIds";

/**
 * One group row. An id the lookup could not resolve still gets a row: the id is all we know about it,
 * and dropping it would hide access the job actually recorded. Such a row gets no type badge — the
 * badge would have to invent a type for a group we could not read.
 *
 * The unread type falls back to the same placeholder as any other missing value rather than a phrase
 * of its own. A bare id in the Group column already says the group could not be read, and naming a
 * cause there would be a guess: failing to read a group proves neither that it was deleted nor that
 * it still exists.
 */
const GroupRow = ({ id, userGroup }: { id: string; userGroup?: UserGroup }) => (
    <Table.TR>
        <Table.TD>{userGroup?.name || id}</Table.TD>
        <Table.TD>{userGroup ? <UserGroupTypeBadge groupType={userGroup.groupType} /> : EMPTY_VALUE}</Table.TD>
        <Table.TD>{userGroup?.description || EMPTY_VALUE}</Table.TD>
    </Table.TR>
);

const GroupTable = ({
    ids,
    userGroupsById,
    testId
}: {
    ids: string[];
    userGroupsById: Record<string, UserGroup>;
    testId: string;
}) => (
    <Table density="loose" data-testid={testId}>
        <Table.THead>
            <Table.TR>
                <Table.TH sorting={{}} width="40%">
                    Group
                </Table.TH>
                <Table.TH sorting={{}} width="20%">
                    Type
                </Table.TH>
                <Table.TH sorting={{}} width="40%">
                    Description
                </Table.TH>
            </Table.TR>
        </Table.THead>
        <Table.TBody>
            {ids.map((id) => (
                <GroupRow key={id} id={id} userGroup={userGroupsById[id]} />
            ))}
        </Table.TBody>
    </Table>
);

/**
 * The target user groups recorded against an upload, for the detail sidebar's Access tab.
 *
 * The job records ids, not names, so they are resolved here — an id tells an admin nothing about the
 * access their imported users were granted. Rejected ids frequently do not resolve at all, because the
 * usual reason a group is rejected is that it was deleted between upload and processing; those render
 * as the bare id rather than disappearing.
 *
 * "Applied" and "rejected" are the wire and domain terms, kept on the props; the headings say "Granted
 * Group Access" and "Failed Group Access", which is what the two lists mean to an admin.
 */
export const BulkUploadAccessTab = ({
    appliedUserGroupIds,
    rejectedUserGroupIds
}: Pick<BulkUpload, "appliedUserGroupIds" | "rejectedUserGroupIds">) => {
    const applied = useMemo(() => appliedUserGroupIds ?? [], [appliedUserGroupIds]);
    const rejected = useMemo(() => rejectedUserGroupIds ?? [], [rejectedUserGroupIds]);

    const ids = useMemo(() => [...applied, ...rejected], [applied, rejected]);

    const { userGroupsById, isLoading } = useUserGroupsByIds({ ids });

    if (!ids.length) {
        return (
            <div className={styles["upload-details__section"]} data-testid="upload-details-access">
                <Typography type="body" data-testid="upload-details-access-empty">
                    No user groups were applied to this upload.
                </Typography>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div data-testid="upload-details-access">
                <TableSkeleton fullWidth />
            </div>
        );
    }

    return (
        <div data-testid="upload-details-access">
            {!!applied.length && (
                <div className={styles["upload-details__section"]}>
                    <Typography tag="h4" type="header4" className="push--bottom">
                        Granted Group Access
                    </Typography>
                    <Typography type="caption" className="push--bottom">
                        Applied to every user processed from this file.
                    </Typography>
                    <GroupTable ids={applied} userGroupsById={userGroupsById} testId="upload-details-applied-groups" />
                </div>
            )}

            {!!rejected.length && (
                <div className={styles["upload-details__section"]}>
                    <Typography tag="h4" type="header4" className="push--bottom">
                        Failed Group Access
                    </Typography>
                    <Typography type="caption" className="push--bottom">
                        There were issues processing the following group IDs. Users were processed without them.
                    </Typography>
                    <GroupTable
                        ids={rejected}
                        userGroupsById={userGroupsById}
                        testId="upload-details-rejected-groups"
                    />
                </div>
            )}
        </div>
    );
};

BulkUploadAccessTab.displayName = "BulkUploadAccessTab";
